import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, LanguageSwitcher } from "@core/components";
import { Icon, images } from "@core/assets";
import { tryGetProfile, useLogout, useMe } from "@core/auth";
import { EmailVerificationBanner } from "@modules/account/verification";
import {
  ensurePublicRound,
  usePublicRound,
} from "@modules/rounds/public-catalog";
import { ensureApplication } from "@modules/applications/dossier";
import { clearPendingRound } from "@modules/applications/_shared";
import { requireGroup } from "./-guards";

/**
 * `/apply/$roundId` — where registration lands: the applicant is in, and the round came with them.
 *
 * ## What this screen is, and what it deliberately is not
 *
 * It is the **waiting room** of the applicant funnel: read the dossier (`/rounds/{id}`), create an
 * account (`/apply/{id}/register`), arrive here signed in but not yet verified. It confirms the
 * account exists, states the one task actually outstanding, and names what comes next.
 *
 * ## It is also the ONE place the dossier gets created
 *
 * The loader creates it the moment the address is confirmed, then redirects — so a verified
 * applicant never sees this screen at all, and an unverified one sees nothing but the banner. That
 * satisfies US-E2-03 (*"a candidat must never have to create their own dossier"*) without a
 * "start my application" button anywhere.
 *
 * **Why here and not on `/verify-email`.** Creating the dossier needs two things at once: a session
 * and a round id. The verification screen has neither guaranteed — the emailed link carries only a
 * token, `confirm-link/` returns no session, and the mail is often opened on another device. This
 * route always has both: the group guard proves the session, and the round is in the path. So
 * verification confirms and redirects HERE, and this is the only place that writes.
 *
 * `POST /applications/` is get-or-create, which is what makes a loader the right home for it: a
 * refresh, a bookmark and a second click all resolve to the same dossier rather than a second one.
 *
 * ## Why the round id is in the URL
 *
 * `POST /applications/` needs `intake_round_id`, and this is the only place it survives the account
 * chain: signup accepts no `next`, so the round rides through login's server-validated `next` into
 * this path. Keeping it in the URL rather than in storage is what makes a refresh, a bookmark and a
 * re-login all land on the same round.
 *
 * ## Guarded on the GROUP only — never on `email_verified`
 *
 * Registration signs the applicant in while still unverified, because the backend permits it and
 * only SUBMISSION checks `email_verified` (US-E2-02: *"draft before verifying, verify before
 * submitting"*). Gating entry here would bounce every freshly-registered user off the page they
 * were just sent to — which is the whole population this screen exists for.
 */
export const Route = createFileRoute("/apply/$roundId")({
  beforeLoad: async ({ context, location, params }) => {
    // Gate 1 — applicant group. Never `email_verified`: registration signs people in unverified,
    // and this screen is the one that tells them to verify.
    await requireGroup(context.queryClient, "applicant", location.pathname);

    /**
     * Gate 2 — the round must be real, publicly visible, and still accepting.
     *
     * This route had no round check at all until now, so `/apply/anything` rendered a landing for a
     * round that did not exist and offered a "read the dossier again" link into a 404. The round id
     * is the only argument this screen takes; it has to be a round.
     *
     * A CONTEXT precondition rather than access control — round ids ship in the anonymous catalogue,
     * so anyone can forge one and this protects nothing. What it buys is that the next gate never
     * tries to create a dossier against a round the applicant could not have come from, and that a
     * dead campaign link returns to the catalogue instead of a plausible-looking dead end.
     *
     * Free: `ensurePublicRound` reads the cache entry `/rounds/{id}` and the register screen already
     * filled on the way here.
     */
    const preview = await ensurePublicRound(
      context.queryClient,
      params.roundId
    );
    if (!preview?.round.accepting_applications) {
      throw redirect({ to: "/website" });
    }

    /**
     * Gate 3 — verified? Then the dossier is due, and this screen has nothing left to say.
     *
     * The profile is already in cache from the guard above, so this costs no request. Reading
     * `email_verified` off it is the only trustworthy source: the application payload's `can_submit`
     * is computed as `can_submit = editable` server-side and reports `true` for an unverified
     * applicant, so it cannot answer this question.
     */
    const profile = await tryGetProfile(context.queryClient);
    if (!profile?.email_verified) return;

    const application = await ensureApplication(
      context.queryClient,
      params.roundId
    );

    /**
     * Creation failed for a reason an applicant cannot act on — most likely `409 intake_closed`,
     * because the deadline can pass between reading the round and confirming an email. Public
     * discovery is advisory; that POST is authoritative.
     *
     * Falling through to render the waiting room is deliberate: it is honest (the account exists,
     * the dossier does not) and it keeps them somewhere with a way onward, rather than bouncing to a
     * catalogue that no longer lists the round they were applying to.
     */
    if (!application) return;

    // The round has been used. Parking it was only ever to survive the verification email.
    clearPendingRound();

    throw redirect({
      to: "/applicant/applications/$applicationId",
      params: { applicationId: application.id },
      // The dossier always opens on Founder for a just-created application — there is nothing
      // to resume yet, so the explicit default is the honest starting point rather than an
      // omitted param TanStack Router would otherwise require anyway.
      search: { step: "founder" },
      replace: true,
    });
  },
  component: ApplyLandingRoute,
});

function ApplyLandingRoute() {
  const { roundId } = Route.useParams();
  const { t } = useTranslation("acquire");
  const { t: tCommon } = useTranslation("common");
  const { data: profile } = useMe();
  const logout = useLogout();

  /**
   * The round, from the same public endpoint and the same cache entry the Aperçu filled — so
   * arriving here straight after registration costs no request.
   *
   * The PUBLIC read, not a staff one: an applicant has no staff credentials, and this screen needs
   * exactly what the Aperçu needed. A missing round only costs the round's name; the confirmation
   * that the account exists is true either way and must not depend on it.
   */
  const { round } = usePublicRound(roundId);

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      {/*
        A signed-in bar. Unlike the pre-auth chrome on `/rounds/{id}` — which carries no account
        affordance at all, because that page must leave no trace — this one is past the wall, so
        signing out is a legitimate and necessary action.

        At 320px the wordmark (~134px) + language switcher (~146px) + a labelled "Sign out" do not
        fit, so the base layout uses the brand MARK and an icon-only control; both grow from `sm`.
      */}
      <header className="flex items-center gap-xs border-b border-(--border-default) bg-surface-card px-md py-3.5 sm:gap-md sm:px-3xl">
        <img
          src={images["mark-primary"]}
          alt="DZ Champions"
          className="h-7 w-auto sm:hidden"
        />
        <img
          src={images["logo-primary"]}
          alt="DZ Champions"
          className="hidden h-10 w-auto sm:block"
        />

        <div className="flex flex-1 items-center justify-end gap-xs sm:gap-md">
          <LanguageSwitcher />
          <Button
            variant="secondary"
            className="shrink-0"
            onPress={() => logout.mutate()}
            isPending={logout.isPending}
            isDisabled={logout.isPending}
            // The visible label is `sr-only` at 320px, so the icon-only control keeps an
            // accessible name at every width.
            aria-label={tCommon("home.sign-out")}
          >
            <Icon name="log-out" size={18} />
            <span className="sr-only sm:not-sr-only">
              {tCommon("home.sign-out")}
            </span>
          </Button>
        </div>
      </header>

      {/*
        ⚠️ `max-w-192` (768px), NOT `max-w-3xl`. This project's `@theme` defines `--spacing-3xl: 40px`,
        which shadows Tailwind's container scale for that key — so `max-w-3xl` resolved to a
        40px-wide page and collapsed every line of text to one word. Named sizing keys
        (`max-w-lg`/`-xl`/`-3xl`…) are landmines here; use the numeric spacing scale.
      */}
      <main className="mx-auto flex w-full max-w-192 flex-col gap-xl px-md py-xl sm:px-xl sm:py-2xl">
        {/*
          The confirmation. A success SURFACE rather than a toast, because a toast is gone in four
          seconds and this is the answer to "did that work?" — the question someone asks after
          handing over a password, and again after a refresh.
        */}
        <section className="flex flex-col gap-sm rounded-xl border border-(--feedback-success-ring) bg-(--feedback-success-ring-outer) p-lg sm:p-xl">
          <span className="text-(--feedback-success-icon)">
            <Icon name="circle-check" size={28} />
          </span>
          <h1 className="display-xs-semibold text-(--text-primary)">
            {/* Greets by name when the profile has arrived, and stays a complete sentence when it
                has not — the confirmation must never wait on a second request to make sense. */}
            {profile?.user.full_name
              ? t("landing.title-named", { name: profile.user.full_name })
              : t("landing.title")}
          </h1>
          <p className="text-md-regular text-(--text-secondary)">
            {round
              ? t("landing.description-round", { round: round.name })
              : t("landing.description")}
          </p>
        </section>

        {/* Renders nothing once verified — so this screen quietly becomes a plain confirmation for a
            returning applicant, with no dead reminder left behind. */}
        <EmailVerificationBanner />

        {/*
          What happens next, stated rather than implied.
          ⚠️ The dossier itself is not built yet, so this names the step instead of linking to it. A
          live-looking control leading nowhere is worse than an honest sentence.
        */}
        <section className="flex flex-col gap-sm rounded-xl border border-(--border-default) bg-surface-card p-lg sm:p-xl">
          <h2 className="text-md-semibold text-(--text-primary)">
            {t("landing.next.title")}
          </h2>
          <p className="text-sm-regular text-(--text-secondary)">
            {t("landing.next.description")}
          </p>
          <p className="text-sm-regular text-(--text-tertiary)">
            {t("landing.next.not-ready")}
          </p>
        </section>

        {/*
          Back to the round they just read. The Aperçu is public and stateless, so re-reading it
          costs nothing and is the only thing there is to do here today — which is exactly why it is
          offered as a quiet link rather than a primary action competing with the verification task.
        */}
        <Link
          to="/rounds/$roundId"
          params={{ roundId }}
          className="inline-flex min-h-11 items-center gap-xs self-start rounded-md text-sm-medium text-(--text-link) underline outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          <Icon name="arrow-left" size={18} />
          {t("landing.back-to-round")}
        </Link>
      </main>
    </div>
  );
}
